import { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Play, Pencil, Trash2, X, Check, Layers } from "lucide-react";
import api from "../lib/api";
import SkinPreview from "./SkinPreview";
import { applyTrailColor } from "../pages/Skins";

/**
 * LoadoutsPanel — saved cosmetic bundles on /skins.
 *
 * A Loadout is { id, name, ball_skin, paddle_skin, trail_color, paddle_accent }.
 * Users save up to `max` of them (server-enforced) and one-click equip any slot
 * to synchronize the four cosmetic state pieces (active_ball_skin,
 * active_skin(paddle), trail_color, paddle_accent) in a single request.
 *
 * Props:
 *   data         — /ball-skins response (catalog + unlocked + active + trail_color)
 *   paddleAccent — current accent hex or null
 *   onApplied    — callback fired after a successful equip so the parent can refresh
 */
export default function LoadoutsPanel({ data, paddleAccent, onApplied }) {
  const [loadouts, setLoadouts] = useState([]);
  const [max, setMax] = useState(6);
  const [editing, setEditing] = useState(null); // null | "new" | loadout.id
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/loadouts");
      setLoadouts(r.data.loadouts || []);
      setMax(r.data.max || 6);
    } catch (_) { /* noop */ }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const equip = async (id) => {
    if (busy) return;
    setBusy(true); setErr(null);
    try {
      await api.post(`/loadouts/${id}/equip`);
      await onApplied?.();
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to equip loadout.");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (busy) return;
    setBusy(true); setErr(null);
    try {
      await api.delete(`/loadouts/${id}`);
      await refresh();
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to delete loadout.");
    } finally { setBusy(false); }
  };

  const saveNew = async (payload) => {
    setBusy(true); setErr(null);
    try {
      await api.post("/loadouts", payload);
      await refresh();
      setEditing(null);
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to save loadout.");
    } finally { setBusy(false); }
  };

  const saveEdit = async (id, payload) => {
    setBusy(true); setErr(null);
    try {
      await api.patch(`/loadouts/${id}`, payload);
      await refresh();
      setEditing(null);
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to save loadout.");
    } finally { setBusy(false); }
  };

  const slotsRemaining = Math.max(0, max - loadouts.length);
  const canAdd = slotsRemaining > 0;

  return (
    <section className="mt-8 glass p-5 border border-white/15" data-testid="loadouts-panel">
      <div className="flex items-end justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div
            className="w-9 h-9 border border-[#b026ff]/60 text-[#b026ff] flex items-center justify-center"
            style={{ boxShadow: "0 0 12px rgba(176,38,255,0.25)" }}
          >
            <Layers className="w-4 h-4" />
          </div>
          <div>
            <div className="font-mono-cosmic text-[10px] tracking-[0.35em] text-[#b026ff] uppercase mb-0.5">
              // LOADOUTS · ONE-CLICK COSMETICS
            </div>
            <div className="font-heading font-black uppercase text-2xl tracking-tighter text-white">
              Saved Presets
            </div>
            <div className="text-white/50 text-xs mt-0.5">
              Bundle your comet, paddle, trail and accent — swap them instantly with one tap.
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => canAdd && setEditing("new")}
          disabled={!canAdd || busy}
          className={`px-4 py-2 border font-mono-cosmic text-[10px] tracking-[0.3em] uppercase transition inline-flex items-center gap-2 ${
            canAdd
              ? "border-[#b026ff] text-[#b026ff] hover:bg-[#b026ff]/10"
              : "border-white/10 text-white/30 cursor-not-allowed"
          }`}
          data-testid="loadout-new-btn"
        >
          <Plus className="w-3.5 h-3.5" />
          New · {loadouts.length}/{max}
        </button>
      </div>

      {err && (
        <div
          className="mb-4 glass p-3 border border-[#ff007f]/60 text-[#ff007f] font-mono-cosmic text-xs tracking-widest uppercase"
          data-testid="loadout-error"
        >
          {err}
        </div>
      )}

      {loadouts.length === 0 && editing !== "new" ? (
        <div
          className="border border-dashed border-white/15 px-4 py-8 text-center font-mono-cosmic text-[11px] tracking-[0.3em] text-white/40 uppercase"
          data-testid="loadouts-empty"
        >
          // NO LOADOUTS YET — SAVE YOUR CURRENT LOOK
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {loadouts.map((ld) => (
            <LoadoutCard
              key={ld.id}
              loadout={ld}
              data={data}
              paddleAccent={paddleAccent}
              busy={busy}
              onEquip={() => equip(ld.id)}
              onEdit={() => setEditing(ld.id)}
              onDelete={() => remove(ld.id)}
            />
          ))}
        </div>
      )}

      {editing && (
        <LoadoutEditor
          initial={
            editing === "new"
              ? {
                  name: `Loadout ${loadouts.length + 1}`,
                  ball_skin: data.active,
                  paddle_skin: null, // frontend currently doesn't expose paddle list here
                  trail_color: data.trail_color || null,
                  paddle_accent: paddleAccent || null,
                }
              : loadouts.find((l) => l.id === editing)
          }
          data={data}
          paddleAccent={paddleAccent}
          busy={busy}
          onClose={() => setEditing(null)}
          onSave={(payload) =>
            editing === "new" ? saveNew(payload) : saveEdit(editing, payload)
          }
        />
      )}
    </section>
  );
}

function LoadoutCard({ loadout, data, paddleAccent, busy, onEquip, onEdit, onDelete }) {
  // Resolve the skin object to render in the preview. If the saved ball_skin
  // isn't in the catalog (user lost access?), fall back to the active one.
  const skin = useMemo(() => {
    const id = loadout.ball_skin || data.active;
    const found = (data.skins || []).find((s) => s.id === id);
    return found ? applyTrailColor(found, loadout.trail_color) : null;
  }, [data, loadout]);

  // Flag mismatch: is this loadout already the currently equipped look?
  const isCurrent =
    loadout.ball_skin === data.active &&
    (loadout.trail_color || null) === (data.trail_color || null) &&
    (loadout.paddle_accent || null) === (paddleAccent || null);

  return (
    <div
      className={`glass p-3 border transition ${
        isCurrent ? "border-[#00ff41]/60 shadow-[0_0_14px_rgba(0,255,65,0.2)]" : "border-white/10 hover:border-[#b026ff]/50"
      }`}
      data-testid={`loadout-card-${loadout.id}`}
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="font-heading uppercase text-sm tracking-tight text-white truncate">
          {loadout.name}
        </div>
        {isCurrent && (
          <span className="font-mono-cosmic text-[8px] tracking-[0.3em] uppercase text-[#00ff41] inline-flex items-center gap-1">
            <Check className="w-3 h-3" /> Equipped
          </span>
        )}
      </div>
      <div className="flex items-center justify-center mb-2">
        {skin ? (
          <SkinPreview skin={skin} size={90} aspect="square" />
        ) : (
          <div className="w-[90px] h-[90px] border border-white/10" />
        )}
      </div>
      <div className="flex items-center gap-1.5 mb-3 flex-wrap font-mono-cosmic text-[9px] tracking-[0.2em] uppercase text-white/50">
        {loadout.trail_color && (
          <span className="inline-flex items-center gap-1">
            <span className="w-2.5 h-2.5 border border-white/20" style={{ background: loadout.trail_color }} />
            Trail
          </span>
        )}
        {loadout.paddle_accent && (
          <span className="inline-flex items-center gap-1">
            <span className="w-2.5 h-2.5 border border-white/20" style={{ background: loadout.paddle_accent }} />
            Paddle
          </span>
        )}
        {loadout.paddle_skin && loadout.paddle_skin !== "default" && (
          <span className="truncate">· {loadout.paddle_skin.replace(/-/g, " ")}</span>
        )}
      </div>
      <div className="grid grid-cols-[1fr_auto_auto] gap-1.5">
        <button
          onClick={onEquip}
          disabled={busy || isCurrent}
          className={`py-1.5 border font-mono-cosmic text-[10px] tracking-[0.25em] uppercase transition inline-flex items-center justify-center gap-1.5 ${
            isCurrent
              ? "border-white/10 text-white/30 cursor-not-allowed"
              : "border-[#00ffff] text-[#00ffff] hover:bg-[#00ffff]/10"
          }`}
          data-testid={`loadout-equip-${loadout.id}`}
        >
          <Play className="w-3 h-3" /> {isCurrent ? "Active" : "Equip"}
        </button>
        <button
          onClick={onEdit}
          disabled={busy}
          className="w-9 border border-white/15 hover:border-white/50 text-white/60 hover:text-white flex items-center justify-center transition"
          data-testid={`loadout-edit-${loadout.id}`}
          title="Edit"
        >
          <Pencil className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={onDelete}
          disabled={busy}
          className="w-9 border border-white/15 hover:border-[#ff007f]/60 text-white/60 hover:text-[#ff007f] flex items-center justify-center transition"
          data-testid={`loadout-delete-${loadout.id}`}
          title="Delete"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

// Cosmic-themed trail color presets — curated palette for quick picks.
// Adding new entries here automatically propagates through both the
// trail color and paddle accent picker UIs without code changes.
const SWATCHES = [
  { id: "default",     label: "Default",       value: null },
  { id: "plasma-pink", label: "Plasma Pink",   value: "#ff007f" },
  { id: "nebula-blue", label: "Nebula Blue",   value: "#00ffff" },
  { id: "solar-gold",  label: "Solar Gold",    value: "#ffd700" },
  { id: "cosmic-purple", label: "Cosmic Purple", value: "#b026ff" },
  { id: "radiant-emerald", label: "Radiant Emerald", value: "#00ff88" },
  { id: "supernova-orange", label: "Supernova Orange", value: "#ff8c1a" },
  { id: "ice-comet",   label: "Ice Comet",     value: "#a8e4ff" },
  { id: "vermillion",  label: "Vermillion",    value: "#ff3b30" },
  { id: "bone-white",  label: "Bone White",    value: "#fff8f0" },
  { id: "neon-mint",   label: "Neon Mint",     value: "#39ff9d" },
  { id: "shadow-violet", label: "Shadow Violet", value: "#7b2cff" },
];

function LoadoutEditor({ initial, data, busy, onClose, onSave }) {
  const [form, setForm] = useState({
    name: initial?.name || "New Loadout",
    ball_skin: initial?.ball_skin || data.active,
    paddle_skin: initial?.paddle_skin || null,
    trail_color: initial?.trail_color || null,
    paddle_accent: initial?.paddle_accent || null,
  });

  // User-saved color presets — refreshed on open + after each save/delete.
  const [userPresets, setUserPresets] = useState([]);
  useEffect(() => {
    api.get("/color-presets")
      .then((r) => setUserPresets(r.data.presets || []))
      .catch(() => setUserPresets([]));
  }, []);
  const refreshPresets = async () => {
    try {
      const r = await api.get("/color-presets");
      setUserPresets(r.data.presets || []);
    } catch {
      /* noop */
    }
  };

  const unlockedSkins = useMemo(
    () => (data.skins || []).filter((s) => (data.unlocked || []).includes(s.id)),
    [data]
  );

  const previewSkin = useMemo(() => {
    const found = unlockedSkins.find((s) => s.id === form.ball_skin) || unlockedSkins[0];
    return found ? applyTrailColor(found, form.trail_color) : null;
  }, [unlockedSkins, form.ball_skin, form.trail_color]);

  const submit = (e) => {
    e.preventDefault();
    onSave({
      name: form.name.trim() || "Loadout",
      ball_skin: form.ball_skin,
      paddle_skin: form.paddle_skin,
      trail_color: form.trail_color,
      paddle_accent: form.paddle_accent,
    });
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-10"
      onClick={onClose}
      data-testid="loadout-editor"
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="glass border border-[#b026ff]/60 w-full max-w-lg mx-4 p-5"
        style={{ boxShadow: "0 0 24px rgba(176,38,255,0.25)" }}
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="font-mono-cosmic text-[10px] tracking-[0.35em] text-[#b026ff] uppercase">
              // {initial?.id ? "EDIT" : "NEW"} LOADOUT
            </div>
            <div className="font-heading font-black uppercase text-xl tracking-tighter text-white">
              Configure Preset
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 border border-white/15 hover:border-white/50 text-white/60 hover:text-white flex items-center justify-center"
            data-testid="loadout-editor-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex items-center justify-center mb-4">
          {previewSkin && <SkinPreview skin={previewSkin} size={100} />}
        </div>

        <label className="block mb-4">
          <div className="font-mono-cosmic text-[9px] tracking-[0.3em] uppercase text-white/50 mb-1">
            Name
          </div>
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            maxLength={32}
            className="w-full bg-black/40 border border-white/15 focus:border-[#b026ff] px-3 py-2 text-white font-mono-cosmic text-sm tracking-wide outline-none transition"
            data-testid="loadout-editor-name"
          />
        </label>

        <div className="mb-4">
          <div className="font-mono-cosmic text-[9px] tracking-[0.3em] uppercase text-white/50 mb-1.5">
            Comet · {unlockedSkins.length} unlocked
          </div>
          <select
            value={form.ball_skin || ""}
            onChange={(e) => setForm((f) => ({ ...f, ball_skin: e.target.value }))}
            className="w-full bg-black/40 border border-white/15 focus:border-[#b026ff] px-3 py-2 text-white font-mono-cosmic text-xs tracking-wide outline-none"
            data-testid="loadout-editor-ball"
          >
            {unlockedSkins.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </div>

        <ColorRow
          label="Trail Color"
          value={form.trail_color}
          onChange={(v) => setForm((f) => ({ ...f, trail_color: v }))}
          testidPrefix="loadout-trail"
          userPresets={userPresets}
          onPresetsChanged={refreshPresets}
        />
        <ColorRow
          label="Paddle Accent"
          value={form.paddle_accent}
          onChange={(v) => setForm((f) => ({ ...f, paddle_accent: v }))}
          testidPrefix="loadout-accent"
          userPresets={userPresets}
          onPresetsChanged={refreshPresets}
        />

        <div className="grid grid-cols-2 gap-2 mt-5">
          <button
            type="button"
            onClick={onClose}
            className="py-2 border border-white/15 text-white/60 hover:text-white font-mono-cosmic text-[10px] tracking-[0.3em] uppercase transition"
            data-testid="loadout-editor-cancel"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy}
            className="py-2 border border-[#b026ff] text-[#b026ff] hover:bg-[#b026ff]/10 font-mono-cosmic text-[10px] tracking-[0.3em] uppercase transition"
            data-testid="loadout-editor-save"
          >
            Save
          </button>
        </div>
      </form>
    </div>
  );
}

function ColorRow({ label, value, onChange, testidPrefix, userPresets = [], onPresetsChanged }) {
  // Match the current value to a curated preset OR a saved user preset
  // so we can show its label.
  const matchedCurated = SWATCHES.find((sw) => (sw.value || null) === (value || null));
  const matchedUser = userPresets.find((p) => p.color.toLowerCase() === (value || "").toLowerCase());
  const matched = matchedCurated || (matchedUser ? { id: matchedUser.id, label: matchedUser.name, value: matchedUser.color } : null);
  const isCustom = value && !matched;
  const HEX_RE = /^#[0-9a-fA-F]{6}$/;
  // Pilot can save the active custom color as a named preset.
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [saving, setSaving] = useState(false);

  const savePreset = async () => {
    const name = saveName.trim();
    if (!name || !value || !HEX_RE.test(value)) return;
    setSaving(true);
    try {
      await api.post("/color-presets", { name, color: value });
      await onPresetsChanged?.();
      setSaveOpen(false);
      setSaveName("");
    } catch (err) {
      // Surface as an inline message under the button row.
      alert(err?.response?.data?.detail || "Couldn't save preset");
    } finally {
      setSaving(false);
    }
  };

  const deletePreset = async (id) => {
    try {
      await api.delete(`/color-presets/${id}`);
      await onPresetsChanged?.();
    } catch {
      /* noop */
    }
  };

  return (
    <div className="mb-3">
      <div className="font-mono-cosmic text-[9px] tracking-[0.3em] uppercase text-white/50 mb-1.5 flex items-center justify-between">
        <span>{label}</span>
        {(matched || isCustom) && (
          <span
            className="text-white/65 normal-case tracking-normal text-[10px]"
            data-testid={`${testidPrefix}-name`}
          >
            {isCustom ? `Custom ${value.toUpperCase()}` : matched.label}
          </span>
        )}
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        {SWATCHES.map((sw) => {
          const active = !isCustom && (value || null) === sw.value;
          return (
            <button
              key={sw.id}
              type="button"
              onClick={() => onChange(sw.value)}
              className="w-8 h-8 border transition flex items-center justify-center"
              style={{
                background: sw.value || "repeating-linear-gradient(45deg, #15151c 0 4px, #2a2a34 4px 8px)",
                borderColor: active ? "#ffffff" : "rgba(255,255,255,0.25)",
                boxShadow: active ? `0 0 10px ${sw.value || "rgba(255,255,255,0.4)"}` : "none",
              }}
              title={sw.label}
              data-testid={`${testidPrefix}-${sw.id}`}
            >
              {active && <Check className="w-3 h-3 text-white drop-shadow" />}
            </button>
          );
        })}
        {/* Custom hex input */}
        <label
          className={`flex items-center gap-1.5 px-2 h-8 border transition cursor-text ${
            isCustom ? "border-white/60" : "border-white/15"
          }`}
          style={{
            boxShadow: isCustom ? `0 0 8px ${value}` : "none",
            background: isCustom ? `${value}15` : "transparent",
          }}
        >
          <span
            className="w-3 h-3"
            style={{
              background: isCustom ? value : "transparent",
              border: "1px solid rgba(255,255,255,0.3)",
            }}
          />
          <input
            type="text"
            value={isCustom ? value : ""}
            placeholder="#hex"
            onChange={(e) => {
              const v = e.target.value.trim();
              if (v === "") {
                onChange(null);
              } else if (HEX_RE.test(v)) {
                onChange(v);
              }
            }}
            className="w-16 bg-transparent font-mono-cosmic text-[10px] uppercase text-white/85 outline-none placeholder:text-white/30"
            data-testid={`${testidPrefix}-custom-hex`}
            maxLength={7}
          />
        </label>
        {/* Save current as preset */}
        {isCustom && !matchedUser && !saveOpen && (
          <button
            type="button"
            onClick={() => setSaveOpen(true)}
            className="px-2 h-8 border border-[#ffd700]/60 hover:bg-[#ffd700]/15 text-[#ffd700] font-mono-cosmic text-[9px] tracking-[0.25em] uppercase transition"
            data-testid={`${testidPrefix}-save-preset`}
          >
            + SAVE
          </button>
        )}
      </div>

      {/* User-saved presets */}
      {userPresets.length > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap mt-2">
          <span className="font-mono-cosmic text-[8px] tracking-[0.3em] uppercase text-white/35">
            MY PRESETS
          </span>
          {userPresets.map((p) => {
            const active = (value || "").toLowerCase() === p.color.toLowerCase();
            return (
              <div
                key={p.id}
                className="relative group"
                title={`${p.name} · ${p.color}`}
              >
                <button
                  type="button"
                  onClick={() => onChange(p.color)}
                  className="w-7 h-7 border transition flex items-center justify-center"
                  style={{
                    background: p.color,
                    borderColor: active ? "#ffffff" : "rgba(255,255,255,0.2)",
                    boxShadow: active ? `0 0 8px ${p.color}` : "none",
                  }}
                  data-testid={`${testidPrefix}-user-preset-${p.id}`}
                >
                  {active && <Check className="w-3 h-3 text-white drop-shadow" />}
                </button>
                <button
                  type="button"
                  onClick={() => deletePreset(p.id)}
                  className="absolute -top-1 -right-1 w-3 h-3 bg-red-600 text-white text-[8px] flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                  title="Delete preset"
                  data-testid={`${testidPrefix}-user-preset-${p.id}-delete`}
                >
                  ×
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Save dialog */}
      {saveOpen && (
        <div className="mt-2 flex items-center gap-2" data-testid={`${testidPrefix}-save-dialog`}>
          <input
            type="text"
            placeholder="Preset name"
            value={saveName}
            onChange={(e) => setSaveName(e.target.value)}
            maxLength={24}
            className="bg-black/40 border border-[#ffd700]/40 px-2 py-1 text-white font-mono-cosmic text-[10px] outline-none"
            autoFocus
          />
          <button
            type="button"
            onClick={savePreset}
            disabled={saving || !saveName.trim()}
            className="px-2 py-1 border border-[#ffd700] bg-[#ffd700]/15 text-[#ffd700] font-mono-cosmic text-[9px] tracking-[0.2em] uppercase disabled:opacity-40"
            data-testid={`${testidPrefix}-save-confirm`}
          >
            {saving ? "…" : "SAVE"}
          </button>
          <button
            type="button"
            onClick={() => { setSaveOpen(false); setSaveName(""); }}
            className="px-2 py-1 border border-white/20 text-white/55 font-mono-cosmic text-[9px] tracking-[0.2em] uppercase"
          >
            CANCEL
          </button>
        </div>
      )}
    </div>
  );
}
